import { useWriteContract, useReadContract } from 'wagmi';
import { MOLT_ESCROW_ADDRESS, MOLT_ESCROW_ABI, USDC_ADDRESS, USDC_ABI } from '../config/contracts';
import { parseUnits, keccak256, toHex } from 'viem';
import { useAccount } from 'wagmi';

/**
 * EscrowService Hook for MoltEscrowV2
 *
 * USDC-based escrow with 2-step deposit flow:
 * 1. approve() - Approve USDC spending
 * 2. deposit() - Deposit USDC into escrow
 *
 * Note: release() and refund() are backend-only (onlyOwner)
 */
export const useEscrow = () => {
  const { writeContractAsync } = useWriteContract();
  const { address } = useAccount();

  /**
   * Convert UUID to bytes32 for on-chain job identification
   * @param uuid Job UUID (e.g., "550e8400-e29b-41d4-a716-446655440000")
   * @returns keccak256 hash as bytes32
   */
  const uuidToBytes32 = (uuid: string): `0x${string}` => {
    const normalized = uuid.replace(/-/g, '').toLowerCase();
    return keccak256(toHex(normalized));
  };

  /**
   * Check USDC allowance for escrow contract
   */
  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: USDC_ADDRESS,
    abi: USDC_ABI,
    functionName: 'allowance',
    args: address ? [address, MOLT_ESCROW_ADDRESS] : undefined,
    query: { enabled: !!address, refetchInterval: 10_000 },
  });

  /**
   * Get user's USDC balance
   */
  const { data: balance, refetch: refetchBalance } = useReadContract({
    address: USDC_ADDRESS,
    abi: USDC_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: { enabled: !!address, refetchInterval: 10_000 },
  });

  /**
   * Step 1: Approve USDC spending
   * @param amountUsdc USDC amount (e.g., "100.50")
   */
  const approveUsdc = async (amountUsdc: string) => {
    const amount = parseUnits(amountUsdc, 6); // USDC has 6 decimals

    return writeContractAsync({
      address: USDC_ADDRESS,
      abi: USDC_ABI,
      functionName: 'approve',
      args: [MOLT_ESCROW_ADDRESS, amount],
    });
  };

  /**
   * Step 2: Deposit USDC into escrow
   * @param jobId Job UUID
   * @param amountUsdc USDC amount (e.g., "100.50")
   */
  const deposit = async (jobId: string, amountUsdc: string) => {
    const jobIdBytes = uuidToBytes32(jobId);
    const amount = parseUnits(amountUsdc, 6); // USDC has 6 decimals

    return writeContractAsync({
      address: MOLT_ESCROW_ADDRESS,
      abi: MOLT_ESCROW_ABI,
      functionName: 'deposit',
      args: [jobIdBytes, amount],
    });
  };

  /**
   * Open a dispute on a job
   * @param jobId Job UUID
   */
  const openDispute = async (jobId: string) => {
    const jobIdBytes = uuidToBytes32(jobId);

    return writeContractAsync({
      address: MOLT_ESCROW_ADDRESS,
      abi: MOLT_ESCROW_ABI,
      functionName: 'dispute',
      args: [jobIdBytes],
    });
  };

  /**
   * Direct USDC transfer to any address (for quiz payments, no escrow involved)
   * ERC20 transfer — no prior approve needed since we're spending from own wallet
   */
  const transferUsdc = async (toAddress: `0x${string}`, amountUsdc: string) => {
    const amount = parseUnits(amountUsdc, 6);
    return writeContractAsync({
      address: USDC_ADDRESS,
      abi: USDC_ABI,
      functionName: 'transfer',
      args: [toAddress, amount],
    });
  };

  return {
    approveUsdc,
    deposit,
    openDispute,
    transferUsdc,
    allowance,
    balance,
    refetchBalance,
  };
};
